import { Stack, Tooltip, TooltipProps } from '@mui/material';
import { CheckOverflow } from '@schooly/components/filters';
import { FC, isValidElement, ReactNode, useCallback, useMemo, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';

import { TagSelect, TagSelectProps } from '../TagSelect/TagSelect';

export interface TruncatedTagSelectItem {
  label: string;
  value?: string | number;
}

const isReactNode = (item: unknown): item is ReactNode => {
  return typeof item === 'object' && item !== null && isValidElement(item);
};

const isTagSelectItem = (item: unknown): item is TruncatedTagSelectItem => {
  return (
    typeof item === 'object' &&
    item !== null &&
    'label' in item &&
    typeof (item as any).label === 'string' &&
    !isReactNode(item)
  );
};

type NormalizedItem =
  | { type: 'string'; label: string; value: string }
  | { type: 'object'; label: string; value?: string | number }
  | { type: 'reactNode'; node: ReactNode };

const normalizeItem = (
  item: string | TruncatedTagSelectItem | ReactNode,
): NormalizedItem | null => {
  if (typeof item === 'string') {
    return { type: 'string', label: item, value: item };
  }
  if (isTagSelectItem(item)) {
    return { type: 'object', ...item };
  }
  if (isReactNode(item)) {
    return { type: 'reactNode', node: item };
  }

  console.warn(`Unsupported item type: ${typeof item}, skipping`);
  return null;
};

type RenderTagProps = {
  item: NormalizedItem;
  tagProps?: Omit<TagSelectProps, 'label' | 'children'>;
  key: string;
};

const renderTag = ({ item, key, tagProps }: RenderTagProps): ReactNode => {
  if (item.type === 'reactNode') {
    return <div key={key}>{item.node}</div>;
  }

  return (
    <div key={key}>
      <TagSelect label={item.label} size="small" variant="outlined" {...tagProps} />
    </div>
  );
};

export interface TruncatedTagSelectProps {
  items: Array<string | TruncatedTagSelectItem | ReactNode>;
  tagProps?: Omit<TagSelectProps, 'label' | 'children'>;
  tooltipProps?: Omit<TooltipProps, 'children' | 'title'>;
  tooltipTitle?: React.ReactNode;
  spacing?: number;
  maxWidth?: number;
}

export const TruncatedTagSelect: FC<TruncatedTagSelectProps> = ({
  items,
  tagProps,
  tooltipProps,
  tooltipTitle,
  spacing = 1,
  maxWidth: propsMaxWidth,
}) => {
  const { ref: containerRef, width: containerWidth } = useResizeDetector<HTMLDivElement>();
  const { ref: counterRef, width: counterWidth } = useResizeDetector<HTMLDivElement>();

  const [hiddenTags, setHiddenTags] = useState<Set<number>>(new Set());
  const setTagHidden = useCallback((index: number, isHidden: boolean) => {
    setHiddenTags((tags) => {
      isHidden ? tags.add(index) : tags.delete(index);
      return new Set(tags);
    });
  }, []);

  const maxWidth = useMemo(() => {
    return (propsMaxWidth ?? containerWidth ?? 0) - (counterWidth ?? 0);
  }, [containerWidth, counterWidth, propsMaxWidth]);

  const normalizedItems = useMemo(() => {
    return items.map((item) => normalizeItem(item)).filter(Boolean) as NormalizedItem[];
  }, [items]);

  const fullTooltipContent = useMemo(() => {
    if (tooltipTitle) return tooltipTitle;

    return (
      <Stack gap={0.5}>
        {normalizedItems.map((item, i) => renderTag({ item, key: `tooltip_item_${i}`, tagProps }))}
      </Stack>
    );
  }, [normalizedItems, tagProps, tooltipTitle]);

  const tagNodes = useMemo(() => {
    const tags = normalizedItems.map((item, i) => renderTag({ item, key: `tag_${i}`, tagProps }));
    return tags.map((tagNode, i) =>
      i === 0 ? (
        tagNode
      ) : (
        <CheckOverflow
          index={i}
          onHideTag={setTagHidden}
          key={`_${i}`}
          maxWidth={maxWidth}
          spacing={spacing}
        >
          {tagNode}
        </CheckOverflow>
      ),
    );
  }, [normalizedItems, tagProps, setTagHidden, maxWidth, spacing]);

  return (
    <Tooltip arrow title={hiddenTags.size > 0 ? fullTooltipContent : ''} {...tooltipProps}>
      <Stack
        ref={containerRef}
        flexDirection="row"
        alignItems="center"
        width="100%"
        sx={{ cursor: 'default' }}
      >
        {tagNodes}
        {!!hiddenTags.size && (
          <TagSelect
            ref={counterRef}
            sx={(theme) => ({ ...theme.typography.body1, ml: 0.5 * spacing })}
            label={`+${hiddenTags.size}`}
            size="small"
            {...tagProps}
          />
        )}
      </Stack>
    </Tooltip>
  );
};
